import React from 'react'
import { cx, dateFormat, dateDiff, dateTimeFormat } from '@/utils/strings'
import ImageContentful from '@/components/ui/image-contentful'
import LinkComponent from '@/components/link-component'
import CtaLink, { buttonStyles } from './ui/cta-link'
import { useTranslations } from '@/utils/locale'

export type ArticleCardProps = {
  description?: string
  image: any
  title: string
  publishDate?: string
  updateDate?: string
  url: string
  cta?: string
  eventDate?: any
  tags?: string[]
  newsfeed?: boolean
  eyebrowText?: string
  attribution?: {
    company: string
    authors?: string[]
  }
}

export const renderTags = (tags: any) => {
  if (!tags || !Array.isArray(tags) || tags.length === 0) return null
  return (
    <div className="mb-4">
      {(tags as unknown as string[]).map((tag, index, array) => (
        <span key={tag} className="text-primaryBlue text-xs text-eyebrow inline-block">
          {tag}
          {index < array.length - 1 && (
            <span className="text-[10px] text-secondary-800 dark:text-secondary-50 mx-2">▪</span>
          )}
        </span>
      ))}
    </div>
  )
}

export default function ArticleListCard({
  description,
  image,
  imageDarkMode,
  title,
  publishDate,
  updateDate,
  eventDate,
  url,
  cta,
  tags = [],
  newsfeed = false,
  eyebrowText,
  attribution,
}: ArticleCardProps) {
  const renderEventDate = (event) => {
    return (
      <>
        {new Date(event) > new Date() ? (
          <span>{dateFormat(event.toString(), 'en-US')}</span>
        ) : (
          <span>
            {dateTimeFormat(event.toString(), 'en-US')} &nbsp; {dateDiff(event.toString(), 'en-US')}
          </span>
        )}
      </>
    )
  }
  const { t } = useTranslations()
  const editorialPublishDate = updateDate || publishDate
  const publishOrUpdate = updateDate ? t('updated') : t('published')
  return (
    <LinkComponent
      to={`${url}`}
      className={cx(
        'block rounded-lg @container border-b-2 border-secondary-100 overflow-hidden',
        'group hover:scale-[1.01] transition-transform duration-300 ease-in-out'
      )}
    >
      <article className={cx('text-left flex flex-col @4xl:flex-row h-full')}>
        <div
          className={cx(
            newsfeed
              ? 'bg-white @4xl:w-2/12 px-6 pt-6 @4xl:py-6 @4xl:pl-6 @4xl:pr-0 dark:@4xl:pr-6'
              : 'bg-indigoBlue @4xl:w-5/12'
          )}
        >
          <div className="flex h-full">
            {image && image?.fields?.file?.contentType !== 'image/svg+xml' && (
              <div
                className={cx(
                  'rounded-t-lg @4xl:rounded-l-lg @4xl:rounded-t-none h-full w-full overflow-hidden aspect-video flex justify-center items-center',
                  newsfeed && '@4xl:items-start'
                )}
              >
                <ImageContentful
                  className={cx(
                    image?.fields?.file?.details?.image?.width &&
                      image?.fields?.file?.details?.image?.height &&
                      image?.fields?.file?.details?.image?.width / image?.fields?.file?.details?.image?.height > 1.2
                      ? 'h-auto'
                      : 'w-auto h-full @4xl:h-auto!'
                  )}
                  {...image?.fields}
                  darkModeImage={imageDarkMode?.fields}
                />
              </div>
            )}
            {image?.fields?.file?.contentType === 'image/svg+xml' && (
              <div
                className={cx(
                  'flex rounded-t-lg @4xl:rounded-l-lg @4xl:rounded-t-none  w-full  aspect-video overflow-hidden items-center',
                  newsfeed ? 'dark:bg-white @4xl:items-start' : 'bg-primaryBlue '
                )}
              >
                <ImageContentful
                  className={cx(
                    '[&_svg]:text-transparent [&_svg]:m-auto [&_svg]:h-auto [&_svg]:w-1/2 @4xl:[&_svg]:w-full'
                  )}
                  {...image?.fields}
                  darkModeImage={imageDarkMode?.fields}
                />
              </div>
            )}
            {!image && (
              <div className="aspect-video h-full w-full rounded-t-lg @4xl:rounded-l-lg @4xl:rounded-t-none bg-midnight-gradient text-center flex items-center justify-center">
                <span className="font-bwi text-[100px] text-white">&#xe932;</span>
              </div>
            )}
          </div>
        </div>
        <div
          className={cx(
            'h-full @4xl:h-auto p-6 relative  bg-white dark:bg-secondary-800 text-secondary-800 dark:text-secondary-50',
            cta && 'pb-16',
            newsfeed ? '@4xl:w-10/12' : '@4xl:w-7/12'
          )}
        >
          {(eyebrowText || eventDate || editorialPublishDate) && (
            <p className="text-sm font-semibold mb-2 uppercase text-secondary-300 dark:text-secondary-400">
              {eyebrowText && <>{eyebrowText}</>}
              {eventDate && <time>{renderEventDate(eventDate)}</time>}
              {editorialPublishDate && (
                <time>{`${publishOrUpdate}:  ${dateFormat(editorialPublishDate, 'en-US')}`}</time>
              )}
            </p>
          )}

          {tags && renderTags(tags)}

          {title && <h2 className={cx('text-2xl')}>{title}</h2>}
          {attribution && (
            <div className="flex gap-3 mb-4">
              <div className="bg-primaryBlue text-white dark:text-secondary-900 rounded-md px-2 inline-block">
                {attribution.company}
              </div>
              {attribution.authors && (
                <span className="text-secondary-500 dark:text-secondary-300">{attribution.authors.join(', ')}</span>
              )}
            </div>
          )}
          {description && <p className={cx(!newsfeed && 'line-clamp-3', 'my-6 text-lg')}>{description}</p>}
          {cta && (
            <div className="absolute bottom-6 right-6 left-6 border-t border-secondary-100">
              <div className="grid justify-items-end pt-6">
                <CtaLink asButton style={buttonStyles.btnArrow} className="text-right">
                  {cta}
                </CtaLink>
              </div>
            </div>
          )}
        </div>
      </article>
    </LinkComponent>
  )
}
